import { text, integer, index } from "drizzle-orm/sqlite-core";
import { defineTable } from "@kubdee/sdk/schema";

// ============================================================================
// marketing_media_assets — Uploaded media (image / video / carousel container)
//
// One row per uploaded asset. The actual bytes live in R2; this table only
// stores the R2 object key (in `url`), MIME metadata, and provider-side
// upload state (for resumable uploads that pre-allocate an ID before the
// content post is published).
//
// `kind` follows the provider-neutral capability vocabulary:
//   - `image`              — single still frame (PNG/JPEG/WebP)
//   - `video`              — long-form video (mp4/mov)
//   - `carousel_container` — placeholder media that fans out children
//   - `reel`               — short-form vertical video (IG Reel, TT-style)
// ============================================================================
export const marketingMediaAssets = defineTable(
  "marketing",
  "media_assets",
  {
    id: text("id").primaryKey(), // ULID
    companyId: text("company_id").notNull(),
    kind: text("kind").notNull(), // image | video | carousel_container | reel
    url: text("url").notNull(), // R2 object key / signed URL
    mimeType: text("mime_type").notNull(),
    fileSizeBytes: integer("file_size_bytes").notNull(),
    width: integer("width"),
    height: integer("height"),
    durationMs: integer("duration_ms"),
    // Some providers (Meta in particular) require a multi-step resumable
    // upload that produces a provider-side media ID BEFORE the post is
    // published. We cache it here so retries can resume.
    providerMediaId: text("provider_media_id"),
    altText: text("alt_text"),
    // Audit columns
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
    deletedAt: text("deleted_at"),
    deletedBy: text("deleted_by"),
  },
  (table) => [
    index("marketing_media_assets_company_id_idx").on(table.companyId),
    index("marketing_media_assets_company_kind_idx").on(
      table.companyId,
      table.kind,
    ),
  ],
);

export type MarketingMediaAsset = typeof marketingMediaAssets.$inferSelect;
export type NewMarketingMediaAsset = typeof marketingMediaAssets.$inferInsert;
